import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Bot, Cpu, Eye, Rocket, Store, Zap, ArrowRight,
  Sparkles, Code2, Play, Upload, BarChart3, Puzzle,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

interface FeatureDetail {
  title: string;
  subtitle: string;
  icon: React.ElementType;
  steps: { label: string; desc: string; icon: React.ElementType }[];
  highlights: string[];
}

const featureDetails: Record<string, FeatureDetail> = {
  "Software Builder": {
    title: "Software Builder",
    subtitle: "Describe your idea and watch it transform into production architecture",
    icon: Bot,
    steps: [
      { label: "Natural Language Input", desc: "Describe what you want to build in plain English", icon: Sparkles },
      { label: "Intent Analysis", desc: "AI understands your product type, scale, and requirements", icon: Bot },
      { label: "Architecture Design", desc: "Complete system blueprint generated automatically", icon: Code2 },
      { label: "Interactive Refinement", desc: "Refine and customize the generated architecture", icon: Puzzle },
    ],
    highlights: ["Broad application type support", "HTML, CSS, and JavaScript output", "Context-aware suggestions", "Blueprint export"],
  },
  "Autonomous Factory": {
    title: "Autonomous Factory",
    subtitle: "Code generation from AI-designed blueprints via 11 sequential pipeline stages",
    icon: Cpu,
    steps: [
      { label: "Blueprint Input", desc: "Takes the approved architecture as input", icon: Code2 },
      { label: "Code Generation", desc: "Generates HTML, CSS, and JavaScript files", icon: Cpu },
      { label: "Testing Suite", desc: "Auto-generates tests for all generated code", icon: BarChart3 },
      { label: "Quality Assurance", desc: "Validates code quality, security, and certification", icon: Zap },
    ],
    highlights: ["HTML/CSS/JS file generation", "Security scan on every build", "Multi-layer validation", "Test coverage"],
  },
  "Live Preview": {
    title: "Live Preview",
    subtitle: "CDN preview URL for every build iteration",
    icon: Eye,
    steps: [
      { label: "CDN Upload", desc: "Build artifacts uploaded to Supabase Storage CDN", icon: Play },
      { label: "Preview URL", desc: "A unique shareable URL is generated for each build", icon: Zap },
      { label: "Device Testing", desc: "Test across desktop, tablet, and mobile viewports", icon: Eye },
      { label: "Share Preview", desc: "Generate shareable links for stakeholder review", icon: Upload },
    ],
    highlights: ["Global CDN delivery", "Responsive testing", "Shareable URLs", "Per-build isolation"],
  },
  "One-Click Deploy": {
    title: "One-Click Deploy",
    subtitle: "Production deployment in seconds with zero configuration",
    icon: Rocket,
    steps: [
      { label: "Pre-deploy Check", desc: "Automated validation of build, tests, and security", icon: BarChart3 },
      { label: "Build Optimization", desc: "Code splitting, minification, and asset optimization", icon: Cpu },
      { label: "Global Distribution", desc: "Deploy to edge nodes worldwide", icon: Rocket },
      { label: "DNS & SSL", desc: "Automatic domain setup and certificate provisioning", icon: Sparkles },
    ],
    highlights: ["Zero-config deployment", "Global CDN", "Auto SSL", "Rollback support"],
  },
  "Marketplace": {
    title: "Marketplace",
    subtitle: "Share and discover components, templates, and full applications",
    icon: Store,
    steps: [
      { label: "Browse Templates", desc: "Discover pre-built applications and components", icon: Store },
      { label: "One-Click Install", desc: "Add components to your project instantly", icon: Puzzle },
      { label: "Publish", desc: "Share your creations with the community", icon: Upload },
      { label: "Monetize", desc: "Earn from premium templates and components", icon: BarChart3 },
    ],
    highlights: ["Publish and monetize your apps", "Revenue sharing", "Version management"],
  },
  "App Intelligence": {
    title: "App Intelligence",
    subtitle: "AI-driven monitoring, testing, and optimization for your applications",
    icon: Zap,
    steps: [
      { label: "Performance Monitoring", desc: "Real-time performance metrics and alerts", icon: BarChart3 },
      { label: "Error Tracking", desc: "Automatic error detection and root cause analysis", icon: Zap },
      { label: "AI Optimization", desc: "Suggestions for performance improvements", icon: Bot },
      { label: "Usage Analytics", desc: "User behavior tracking and funnel analysis", icon: Eye },
    ],
    highlights: ["Real-time build dashboards", "Build validation scoring", "Error classification"],
  },
};

interface Props {
  featureTitle: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

/**
 * Whether this panel has anything to show for a title.
 *
 * The panel is keyed by display title and its entries are older than the current feature
 * cards, so four of the six cards ("Intent Engine", "Autonomous Build Pipeline",
 * "Self-Healing Repair", "Build Monitoring") match nothing here and the dialog returns null.
 * The card still rendered "Learn more" and a pointer cursor, which is a dead affordance.
 * Callers use this to offer the affordance only where a panel genuinely opens, rather than
 * inventing content to fill the gap.
 */
export function hasFeatureDetail(title: string | null): boolean {
  return !!title && title in featureDetails;
}

export function FeatureInteractionPanel({ featureTitle, open, onOpenChange }: Props) {
  const { t } = useI18n();
  if (!featureTitle) return null;
  const detail = featureDetails[featureTitle];
  if (!detail) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <div className="h-10 w-10 rounded-xl gradient-primary flex items-center justify-center">
              <detail.icon className="h-5 w-5 text-primary-foreground" />
            </div>
            {detail.title}
          </DialogTitle>
          <p className="text-muted-foreground text-sm">{detail.subtitle}</p>
        </DialogHeader>

        <div className="mt-4 space-y-3">
          <h4 className="text-xs font-semibold tracking-widest uppercase text-primary">{t("feature.howItWorks")}</h4>
          {detail.steps.map((step, i) => (
            <motion.div
              key={step.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="flex items-start gap-3 p-3 rounded-xl border border-border/40 bg-muted/20"
            >
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <step.icon className="h-4 w-4 text-primary" />
              </div>
              <div>
                <span className="font-semibold text-sm text-foreground">{step.label}</span>
                <p className="text-xs text-muted-foreground mt-0.5">{step.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-4">
          <h4 className="text-xs font-semibold tracking-widest uppercase text-primary mb-3">{t("feature.highlights")}</h4>
          <div className="flex flex-wrap gap-2">
            {detail.highlights.map((h) => (
              <span key={h} className="text-xs px-3 py-1.5 rounded-full bg-primary/10 text-primary font-medium">{h}</span>
            ))}
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <Button asChild className="flex-1">
            <Link to="/signup">{t("feature.try", { title: detail.title })} <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("feature.close")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
